import React from 'react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts'
import { Widget } from '../Widget'
import { useChartPalette, ChartTooltipContent, formatShortDate, formatLongDate, ChartEmptyState } from './chartUtils'

export interface BarChartSeries {
  key: string
  name: string
  color: string
  /** Formats this series' tooltip value, e.g. formatCurrency for a dollar series. Defaults to toLocaleString(). */
  valueFormatter?: (value: number) => string
}

export interface BarChartWidgetProps {
  title: string
  description?: string
  headerRight?: React.ReactNode
  data: Record<string, any>[]
  xKey: string
  series: BarChartSeries[]
  height?: number
  dateAxis?: boolean
}

// Same minimal language as AreaChartWidget: horizontal-only grid, no
// axis/tick lines, rounded bar tops, small dot+label legend inside the
// chart's own surface (not the lighter shell below it). Mirrors
// admin-dashboard's BarChartWidget so charts look identical across apps.
export default function BarChartWidget({
  title,
  description,
  headerRight,
  data,
  xKey,
  series,
  height = 260,
  dateAxis = true,
}: BarChartWidgetProps) {
  const palette = useChartPalette()
  const valueFormatters = Object.fromEntries(
    series.filter((s) => s.valueFormatter).map((s) => [s.key, s.valueFormatter!])
  )

  return (
    <Widget>
      <Widget.Header>
        <Widget.Title>{title}</Widget.Title>
        {description && <Widget.Description>{description}</Widget.Description>}
        {headerRight}
      </Widget.Header>
      <Widget.Content>
        {data.length === 0 ? (
          <ChartEmptyState height={height} />
        ) : (
          <>
            <ResponsiveContainer width="100%" height={height}>
              <BarChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={palette.border} opacity={0.5} />
                <XAxis
                  dataKey={xKey}
                  stroke={palette.muted}
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  minTickGap={30}
                  tickFormatter={dateAxis ? formatShortDate : undefined}
                />
                <YAxis stroke={palette.muted} fontSize={11} fontFamily="var(--font-mono)" tickLine={false} axisLine={false} width={36} allowDecimals={false} />
                <Tooltip
                  content={<ChartTooltipContent labelFormatter={dateAxis ? formatLongDate : undefined} valueFormatters={valueFormatters} />}
                  cursor={{ fill: palette.border, opacity: 0.25 }}
                />
                {series.map((s) => (
                  <Bar key={s.key} dataKey={s.key} name={s.name} fill={s.color} radius={[4, 4, 0, 0]} maxBarSize={28} />
                ))}
              </BarChart>
            </ResponsiveContainer>
            {series.length > 1 && (
              <Widget.Legend className="pt-3">
                {series.map((s) => (
                  <Widget.Legend.Item key={s.key} color={s.color} label={s.name} />
                ))}
              </Widget.Legend>
            )}
          </>
        )}
      </Widget.Content>
    </Widget>
  )
}
